import { FeatureFlag } from '../CommonSelectors/CommonArea.types';

export enum PdfSectionType {
  CoverPage = 'CoverPage',
  SectionTitle = 'SectionTitle',
  Disclosures = 'Disclosures',
  Summary = 'Summary',
  ActionSteps = 'ActionSteps',
  NetworthSummary = 'NetWorthSummary',
  NetworthDetailed = 'NetWorthDetailed',
  ProjectedCashflow = 'ProjectedCashFlow',
  ProjectedBalanceSheet = 'ProjectedBalanceSheet',
  RetirementAssets = 'RetirementAssets',
  RetirementIncome = 'RetirementExpenses',
  RetirementMonteCarlo = 'RetirementMonteCarlo',
  RetirementMarketScenarios = 'RetirementMarketScenarios',
  EmergencyFund = 'EmergencyFundGoal',
  Education = 'EducationGoals',
  LifeInsurance = 'LifeInsurance',
  DisabilityInsurance = 'DisabilityInsurance',
  LongTermCareInsurance = 'LongTermCareInsurance',
  BuyAHouse = 'BuyAHouseGoal',
  LargePurchase = 'LargePurchaseGoals',
  DebtManagement = 'DebtManagementGoal',
  AdvancedReport = 'AdvancedReport',
  AssetAllocation = 'AssetAllocation',
  HealthCare = 'HealthCareGoal',
  Assumptions = 'Assumptions',
  GoalSummary = 'GoalSummary',
  EstatePlanning = 'EstatePlanningGoal',
  EstatePlanningGoalBeneficiaries = 'EstatePlanningGoalBeneficiaries',
  EstatePlanningGoalEssentialRoles = 'EstatePlanningGoalEssentialRoles',
  RiskToleranceWorkflow = 'RiskToleranceWorkflow',
  ComparePlans = 'ComparePlans',
  RiskProfile3d = 'RiskProfile3d',
  ProtectLiveDream = 'ProtectLiveDream',
  BeFi20 = 'BeFi20',
  PulseCheck = 'PulseCheck',
  EstatePlanningFlow = 'EstatePlanningFlow',
  InsuranceSummary = 'InsuranceSummary',
  CharitableGivingGoals = 'CharitableGivingGoals',
  RetirementLongevityAnalysis = 'RetirementLongevityAnalysis',
  RetirementRothConversion = 'RetirementRothConversion',
  LivingBalanceSheet = 'LivingBalanceSheet',
  EquityCompensationSummary = 'EquityCompensationSummary',
  RetirementSocialSecurity = 'RetirementSocialSecurity',
  SignaturePage = 'SignaturePage',
}

export interface PdfCommonSection {
  type: Exclude<
    PdfSectionType,
    | PdfSectionType.CoverPage
    | PdfSectionType.SectionTitle
    | PdfSectionType.LifeInsurance
    | PdfSectionType.AdvancedReport
    | PdfSectionType.ComparePlans
    | PdfSectionType.BeFi20
    | PdfSectionType.ProtectLiveDream
    | PdfSectionType.LivingBalanceSheet
    | PdfSectionType.RetirementRothConversion
    | PdfSectionType.SignaturePage
  >;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
}

export interface PdfLivingBalanceSheet {
  type: PdfSectionType.LivingBalanceSheet;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    showLayer1: boolean;
    showLayer2: boolean;
    showLayer3: boolean;
    showLegacyNetWorthDetailed: boolean;
    showLegacyNetWorthSummary: boolean;
  };
  coverPageName: string;
}
export interface PdfCoverSection {
  type: PdfSectionType.CoverPage;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    title: string;
    signature: string;
    date: string;
  };
  coverPageName: string;
}

export interface PdfSectionTitleSection {
  type: PdfSectionType.SectionTitle;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    title: string;
    body: string;
  };
}

export interface PdfLifeInsuranceSection {
  type: PdfSectionType.LifeInsurance;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    recommendations: string[];
    owners: { id: number; name: string }[];
    selectedRecommendations: string[];
    selectedOwners: number;
  };
}

export interface PdfAdvancedReportSection {
  type: PdfSectionType.AdvancedReport;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    selectedReport: string;
  };
}

export interface PdfComparePlansSection {
  type: PdfSectionType.ComparePlans;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    plans: { id: string; name: string }[];
    selectedPlans: string[];
  };
}

export interface PdfBeFi20Section {
  type: PdfSectionType.BeFi20;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields?: {
    plans: { id: string; name: string; similarity: 'Low' | 'Varied' | 'High' }[];
    selectedComparisons: string[];
  };
}

export interface AssetBucketingSection {
  type: PdfSectionType.ProtectLiveDream;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields?: {
    showFullBreakdown: boolean;
  };
}

export interface PdfRothConversionSection {
  type: PdfSectionType.RetirementRothConversion;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields?: {
    assumptions: {
      conversionNumber: number;
      items: { label: string; value: string }[];
    }[];
    planImpact: { label: string; current: string; proposed: string }[];
  };
}

export interface PdfSignaturePageSection {
  type: PdfSectionType.SignaturePage;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  canEditTitleAndBody?: boolean;
  displayTitle?: string;
  displayBody?: string;
  displaySignatureFields?: { id?: string; label: string }[];
  editableFields?: {
    title?: string;
    body?: string;
    signatureFields: { id?: string; label: string }[];
  };
}

export type PdfSection =
  | PdfCoverSection
  | PdfSectionTitleSection
  | PdfLifeInsuranceSection
  | PdfAdvancedReportSection
  | PdfComparePlansSection
  | PdfBeFi20Section
  | AssetBucketingSection
  | PdfRothConversionSection
  | PdfSignaturePageSection
  | PdfCommonSection
  | PdfLivingBalanceSheet;

export interface PdfModel {
  includedSections: PdfSection[];
  excludedSections: PdfSection[];
}

export interface WhiteLabelData {
  logo: string;
  title: string;
  inlineImgStyle: string;
}

export interface PdfReducer {
  client: any;
  loadedComponents: PdfSectionType[];
  loadedAdvancedReportsSections: string[];
  featureFlags: FeatureFlag[];
}
